import { Box, Dialog, Grid, Typography } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';

import { StatusCode } from '../../../../../../../../api/enumerations';
import { updateTypologyGroup } from '../../../../../../../../api/theHive/typology';
import {
  BillingUnit,
  priceFieldLabel,
  ReportType,
  TypologyCategory,
  TypologyPriceType,
  TypologypropertyType,
  TypologyRuleKeyword,
} from '../../../../../../../../api/theHive/typology/enumerations';
import {
  EditTypologyGroupData,
  ListTypologyData,
  TypologyData,
} from '../../../../../../../../api/theHive/typology/types';
import {
  CustomFormControl,
  CustomTextField,
  PreffixNumericTextField,
  SelectTextFieldString,
} from '../../../../../../../../components/CustomInput';
import { WhiteCircularProgress } from '../../../../../../../../components/UI/Typography';
import { IconEditMS } from '../../../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../../../context/global';
import useErrorMessage from '../../../../../../../../hooks/useErrorMessage';
import {
  selectBillingUnit,
  selectPriceType,
  selectReportType,
  selectTypologyCategory,
} from '../../constants';
import {
  CancelButton,
  SubsectionTitle,
  SaveButton,
  TitleMoldal,
} from '../../styles';
import {
  ALL_SIZES,
  EditableTypologyGroupFields,
  ITypologyModalProps,
  Typology,
  TypologyGroup,
} from '../../types';
import { convertTypologyGroup } from '../../utils';
import TypologyGroupSLA from '../TypologyGroupSLA';
import TypologyItem from '../TypologyItem';
import {
  DialogContainer,
  StyledDialog,
  StyledPaper,
  ScrollableGrid,
} from './styles';

interface EditTypologyModalProps extends ITypologyModalProps {
  typologyGroup: ListTypologyData;
  open: boolean;
  handleClose: () => void;
}

export function EditTypologyModal({
  typologyGroup,
  fetchTypologyGroups,
  open,
  handleClose,
}: EditTypologyModalProps): JSX.Element {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [formattedTypologyGroup, setFormattedTypologyGroup] =
    useState<TypologyGroup>(convertTypologyGroup(typologyGroup));

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const handleReset = (): void => {
    setFormattedTypologyGroup(convertTypologyGroup(typologyGroup));
  };

  const closeDialog = (): void => {
    handleClose();
    handleReset();
  };

  const handleGroupChange = useCallback(
    <K extends keyof EditableTypologyGroupFields>(
      field: K,
      value: EditableTypologyGroupFields[K]
    ): void => {
      setFormattedTypologyGroup((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleTypologyChange = useCallback(
    <K extends keyof Typology>(
      typologyIndex: number,
      field: K,
      value: Typology[K]
    ) => {
      setFormattedTypologyGroup((prev) => ({
        ...prev,
        typologies: prev.typologies.map((typology, index) =>
          index === typologyIndex ? { ...typology, [field]: value } : typology
        ),
      }));
    },
    []
  );

  const isTypologyGroupValid = useMemo(() => {
    const group = formattedTypologyGroup;
    const identificationValid =
      !!group.category &&
      !!group.code.trim() &&
      !!group.name.trim() &&
      !!group.reportType &&
      !!group.billingUnit &&
      !!group.priceType &&
      group.price > 0;

    if (!identificationValid || group.typologies.length === 0) return false;

    return group.typologies.every((typology) => {
      if (typology.type === TypologypropertyType.SELECT) return false;
      if (typology.range === TypologyRuleKeyword.SELECT) return false;
      if (typology.range === ALL_SIZES) return true;
      if (!typology.area) return false;
      if (typology.range === TypologyRuleKeyword.BETWEEN) {
        return !!typology.area2;
      }
      return true;
    });
  }, [formattedTypologyGroup]);

  const buildTypology = (typology: Typology): TypologyData => {
    if (typology.range === ALL_SIZES) {
      return {
        real_estate_type: typology.type,
        rule_keyword: TypologyRuleKeyword.OVER,
        rule_first_value: 1,
      };
    }
    return {
      real_estate_type: typology.type,
      rule_keyword: typology.range as TypologyRuleKeyword,
      rule_first_value: Number(typology.area),
      ...(typology.range === TypologyRuleKeyword.BETWEEN && {
        rule_second_value: Number(typology.area2),
      }),
    };
  };

  const handleUpdateTypologyGroup = async (): Promise<void> => {
    if (!isTypologyGroupValid) {
      setSnackbarMessage(
        'Todos os campos do grupo de serviço devem ser preenchidos.'
      );
      setErrorMessage(true);
      setOpenSnackbar(true);
      return;
    }

    const group = formattedTypologyGroup;
    const formattedTypologies: TypologyData[] = group.typologies
      .filter((t) => t.visible)
      .map(buildTypology);

    const data: EditTypologyGroupData = {
      name: group.name,
      price: group.price,
      code: group.code,
      category: group.category as TypologyCategory,
      report_type: group.reportType as ReportType,
      billing_unit: group.billingUnit as BillingUnit,
      price_type: group.priceType,
      deadline_by_project: group.deadlineByProject,
      typologies: formattedTypologies,

      ...(group.slaAlertDays > 0 && {
        sla_alert_days: group.slaAlertDays,
      }),

      ...(group.deadlineByProject
        ? { sla_limit_days: null }
        : group.slaLimitDays > 0 && { sla_limit_days: group.slaLimitDays }),

      ...(group.slaUseBusinessDay !== null && {
        sla_use_business_days: group.slaUseBusinessDay,
      }),
    };

    try {
      setSubmitLoading(true);
      const response = await updateTypologyGroup(group.id, data);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      setSnackbarMessage('Serviço editado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      fetchTypologyGroups();
      closeDialog();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <StyledDialog open={open} onClose={() => setConfirmCloseOpen(true)}>
        <DialogContainer>
          <TitleMoldal>
            {IconEditMS} Editar {formattedTypologyGroup.name}
          </TitleMoldal>
          <StyledPaper>
            <ScrollableGrid item container>
              <Grid item container>
                <SubsectionTitle>Grupo de serviço:</SubsectionTitle>

                {/* Identificação */}
                <Grid item container spacing={2}>
                  <Grid item xs={4}>
                    <SelectTextFieldString
                      required
                      id="edit-category"
                      label="categoria"
                      value={formattedTypologyGroup.category}
                      setValue={(v) =>
                        handleGroupChange('category', v as TypologyCategory)
                      }
                      selectOptions={selectTypologyCategory()}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <CustomTextField
                      required
                      id="edit-code"
                      label="código"
                      value={formattedTypologyGroup.code}
                      setValue={(v) => handleGroupChange('code', v)}
                      maxLength={40}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <CustomTextField
                      required
                      id="edit-name"
                      label="nome"
                      value={formattedTypologyGroup.name}
                      setValue={(v) => handleGroupChange('name', v)}
                      maxLength={60}
                    />
                  </Grid>
                </Grid>

                {/* Laudo / cobrança / modelo de valor */}
                <Grid item container spacing={2} mt="4px">
                  <Grid item xs={4}>
                    <SelectTextFieldString
                      required
                      id="edit-report-type"
                      label="tipo de laudo"
                      value={formattedTypologyGroup.reportType}
                      setValue={(v) =>
                        handleGroupChange('reportType', v as ReportType)
                      }
                      selectOptions={selectReportType()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectTextFieldString
                      required
                      id="edit-billing-unit"
                      label="cobrança por"
                      value={formattedTypologyGroup.billingUnit}
                      setValue={(v) =>
                        handleGroupChange('billingUnit', v as BillingUnit)
                      }
                      selectOptions={selectBillingUnit()}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <SelectTextFieldString
                      required
                      id="edit-price-type"
                      label="modelo de valor"
                      value={formattedTypologyGroup.priceType}
                      setValue={(v) =>
                        handleGroupChange('priceType', v as TypologyPriceType)
                      }
                      selectOptions={selectPriceType()}
                    />
                  </Grid>
                </Grid>

                {/* Valor */}
                <Grid item container spacing={2} mt="4px">
                  <Grid item xs={4}>
                    <PreffixNumericTextField
                      required
                      id="edit-price"
                      label={priceFieldLabel(formattedTypologyGroup.priceType)}
                      prefix="R$ "
                      decimalSeparator=","
                      decimalScale={2}
                      maxLength={16}
                      value={formattedTypologyGroup.price}
                      setValue={(v) => handleGroupChange('price', v)}
                    />
                  </Grid>
                </Grid>

                {/* Descrição (tipos de serviço) */}
                <Grid item container mt="20px">
                  <SubsectionTitle>
                    Descrição (tipos de serviço):
                  </SubsectionTitle>
                </Grid>
                {formattedTypologyGroup.typologies.map(
                  (typology, typologyIndex) => {
                    return (
                      <TypologyItem
                        key={typology.id}
                        typology={typology}
                        typologyIndex={typologyIndex}
                        onTypologyChange={handleTypologyChange}
                      />
                    );
                  }
                )}

                {/* Prazo */}
                <Grid item container mt="20px">
                  <CustomFormControl
                    label="prazo definido por projeto"
                    isChecked={formattedTypologyGroup.deadlineByProject}
                    setIsChecked={(v) =>
                      handleGroupChange('deadlineByProject', v)
                    }
                  />
                </Grid>
              </Grid>
            </ScrollableGrid>
            <TypologyGroupSLA
              group={formattedTypologyGroup}
              onGroupChange={handleGroupChange}
            />
          </StyledPaper>
          <Grid item container justifyContent="center" mt={2}>
            <Grid item mr={2}>
              <CancelButton onClick={() => setConfirmCloseOpen(true)}>
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item ml={2}>
              <SaveButton
                disabled={submitLoading}
                onClick={handleUpdateTypologyGroup}
              >
                {submitLoading ? <WhiteCircularProgress size={22} /> : 'Salvar'}
              </SaveButton>
            </Grid>
          </Grid>
        </DialogContainer>
      </StyledDialog>

      {/* Confirmação ao fechar */}
      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
      >
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 460,
          }}
        >
          <TitleMoldal>Deseja fechar?</TitleMoldal>
          <Typography sx={{ color: '#595959' }}>
            As alterações não salvas serão perdidas.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <CancelButton onClick={() => setConfirmCloseOpen(false)}>
              Não
            </CancelButton>
            <SaveButton
              onClick={() => {
                setConfirmCloseOpen(false);
                closeDialog();
              }}
            >
              Sim
            </SaveButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
